import { useSyncExternalStore } from 'react';
import { Button, Popover, Typography } from 'antd';
import { Link } from 'react-router';
import { getLastCorrelationId, subscribeToCorrelationId } from '../../api/client';
import { useCan } from '../../auth/use-session';

/**
 * "Laporkan masalah" — the support code, available at any moment rather than
 * only after a crash (docs/07-implementation-roadmap.md F6.7, docs/90 §2.5).
 *
 * The error boundary already shows a correlation ID when something *threw*
 * (F0.6). This is for the other report, which is the more common one: "this
 * number looks wrong", "this member should be here". Nothing failed, so there is
 * no error to read an ID off — the affordance surfaces the last response the
 * client saw, which is the request that drew what the staff member is looking
 * at.
 *
 * What a copied code buys today is a `docker compose logs api` grep and, for a Super
 * Admin, an `/audit-logs?correlationId=` lookup that names every row that one
 * request wrote. The Kibana search the roadmap mentions waits on backend 7.5, so
 * the copy here does not promise it.
 */
export function SupportCodeButton() {
  const can = useCan();
  const correlationId = useSyncExternalStore(subscribeToCorrelationId, getLastCorrelationId, getLastCorrelationId);

  // Before the first request there is nothing to quote. The button is absent
  // rather than disabled: an affordance that explains it has nothing to offer
  // is worse than one that appears when it does.
  if (!correlationId) return null;

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      title="Kode dukungan"
      content={
        <div style={{ maxWidth: 340 }}>
          <Typography.Paragraph style={{ marginBottom: 8 }}>
            <Typography.Text code copyable={{ text: correlationId }}>
              {correlationId}
            </Typography.Text>
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Kode ini menandai permintaan terakhir dari halaman ini. Sertakan saat melapor, beserta apa yang sedang Anda
            lakukan — dengan keduanya, kejadiannya dapat ditelusuri di catatan server.
          </Typography.Paragraph>
          {can('auditLogs.read') ? (
            <Link to={`/catatan-audit?correlationId=${correlationId}`}>Lihat perubahan dari permintaan ini</Link>
          ) : null}
        </div>
      }
    >
      <Button type="text" size="small">
        Laporkan masalah
      </Button>
    </Popover>
  );
}
